/**
 * The virtualized table.
 *
 * "The list view is the product. Most users will spend 80% of their time
 * there." It renders the ~40 rows that fit plus an overscan margin against a
 * page of up to 200, so `pnpm db:seed:bulk`'s 5,000 opportunities scroll
 * without lag.
 *
 * Selection exists for the manager bulk actions and is deliberately not
 * persisted: a selection that survived a filter change would act on rows the
 * user can no longer see.
 *
 * **Widths are laid out by a `<colgroup>`, not by the cells.** The table is
 * `table-fixed`: without it the browser treats a column width as a suggestion
 * and sizes to content anyway, so a drag would appear to do nothing. The
 * trailing filler column is what makes the fixed layout exact — with at least
 * one auto-width column, any space left over goes there instead of being
 * shared out over the columns the user just sized. When the widths add up to
 * more than the viewport the table grows past `w-full` and the wrapper scrolls
 * sideways.
 */

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useVirtualRows } from '@/lib/virtual';
import {
  clampColumnWidth,
  COLUMNS,
  MAX_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  type ColumnDef,
  type Row,
} from './columns';
import { cn } from '@/lib/utils';

const ROW_HEIGHT = 44;

/** The checkbox column `selectable` renders. Not resizable — it holds one box. */
const SELECT_COLUMN_WIDTH = 40;

/** `px-2` on both sides, plus a pixel of slack so a fit does not clip. */
const CELL_PADDING = 20;

/** The sort arrow and its gap, which the header label sits beside. */
const SORT_ICON_WIDTH = 20;

/** How far one arrow key moves an edge. */
const KEYBOARD_STEP = 16;

export interface OpportunityTableProps {
  rows: Row[];
  columnIds: string[];
  /** Per-column widths in pixels. A column absent here uses its default. */
  columnWidths?: Record<string, number>;
  /** Called once per gesture, when the edge is let go — not on every pixel. */
  onResizeColumn?: (id: string, width: number) => void;
  sort: string | null;
  onSort: (sortKey: string) => void;
  selected: Set<string>;
  onSelect: (id: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  selectable: boolean;
}

export function OpportunityTable({
  rows,
  columnIds,
  columnWidths,
  onResizeColumn,
  sort,
  onSort,
  selected,
  onSelect,
  onSelectAll,
  selectable,
}: OpportunityTableProps): React.ReactElement {
  const columns = useMemo(
    () =>
      columnIds
        .map((id) => COLUMNS.find((column) => column.id === id))
        .filter((column): column is ColumnDef => column !== undefined),
    [columnIds],
  );

  const { ref, window } = useVirtualRows<HTMLDivElement>({
    count: rows.length,
    rowHeight: ROW_HEIGHT,
  });

  /**
   * The width under the pointer mid-drag. Held here rather than pushed to the
   * parent on every `mousemove` so that one gesture is one state write and one
   * `localStorage` write, not one per pixel.
   */
  const [draft, setDraft] = useState<{ id: string; width: number } | null>(null);

  /** The column a double-click asked to fit, while it is being measured. */
  const [fitting, setFitting] = useState<ColumnDef | null>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  /** The width a column has when nothing is being dragged. */
  const settledWidthOf = (column: ColumnDef): number =>
    clampColumnWidth(columnWidths?.[column.id] ?? column.width);

  const widthOf = (column: ColumnDef): number =>
    draft !== null && draft.id === column.id ? draft.width : settledWidthOf(column);

  /**
   * A gesture that did not move the edge is not a resize. Without this, the
   * two mousedown/mouseup pairs inside a double-click would each write the
   * unchanged width back — turning a column still on its default into one
   * pinned at that number, moments before the fit replaces it anyway.
   */
  const commit = (column: ColumnDef, width: number): void => {
    setDraft(null);
    const next = clampColumnWidth(width);
    if (next === settledWidthOf(column)) return;
    onResizeColumn?.(column.id, next);
  };

  /**
   * **Fit-to-contents means the loaded page, not the rendered window.**
   *
   * The table is virtualized, so only ~40 of the page's rows are in the DOM at
   * any moment and measuring those would give the same double-click a
   * different answer depending on where the user had scrolled to. Instead the
   * cells of every loaded row are rendered once, off-screen, measured, and
   * thrown away — so the answer depends only on the page, and the width is
   * capped at `MAX_COLUMN_WIDTH`. Rows on other pages are not consulted; they
   * are not in the browser.
   */
  useLayoutEffect(() => {
    if (fitting === null) return;
    const container = measureRef.current;
    if (container === null) return;

    let widest = 0;
    for (const cell of container.querySelectorAll<HTMLElement>('[data-measure-cell]')) {
      widest = Math.max(widest, cell.scrollWidth);
    }

    const column = fitting;
    setFitting(null);
    // A zero measurement — jsdom, or a container that has not laid out — would
    // otherwise collapse the column to the minimum. Leaving it alone is the
    // honest answer to "cannot measure".
    if (widest === 0) return;
    const next = clampColumnWidth(widest + CELL_PADDING);
    if (next !== settledWidthOf(column)) onResizeColumn?.(column.id, next);
  }, [fitting, onResizeColumn]);

  const visible = rows.slice(window.startIndex, window.endIndex);
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(String(row['id'])));
  const cellCount = columns.length + (selectable ? 1 : 0) + 1;

  return (
    <div ref={ref} className="max-h-[calc(100vh-19rem)] overflow-auto rounded-lg border bg-card">
      <table className="w-full table-fixed border-collapse text-sm">
        <colgroup>
          {selectable && <col style={{ width: SELECT_COLUMN_WIDTH }} />}
          {columns.map((column) => (
            <col key={column.id} data-column-id={column.id} style={{ width: widthOf(column) }} />
          ))}
          {/* Absorbs whatever is left over, so the sized columns stay exact. */}
          <col data-column-id="__filler" />
        </colgroup>

        <thead className="sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_hsl(var(--border))]">
          <tr>
            {selectable && (
              <th scope="col" className="px-2 py-2">
                <input
                  type="checkbox"
                  aria-label="Select every row on this page"
                  className="h-4 w-4"
                  checked={allSelected}
                  onChange={(event) => onSelectAll(event.target.checked)}
                />
              </th>
            )}
            {columns.map((column) => {
              const active = sort === column.sortKey || sort === `-${column.sortKey}`;
              const descending = sort === `-${column.sortKey}`;
              return (
                <th
                  key={column.id}
                  scope="col"
                  className={cn(
                    'relative overflow-hidden whitespace-nowrap px-2 py-2 text-left text-xs font-medium text-muted-foreground',
                    column.align === 'right' && 'text-right',
                  )}
                  aria-sort={active ? (descending ? 'descending' : 'ascending') : undefined}
                >
                  <span className="block truncate" title={column.label}>
                    {/* Every column sorts — `sortKey` is required on ColumnDef. */}
                    <button
                      type="button"
                      className="inline-flex max-w-full items-center gap-1 align-bottom hover:text-foreground"
                      onClick={() => onSort(column.sortKey)}
                    >
                      <span className="truncate">{column.label}</span>
                      {active &&
                        (descending ? (
                          <ArrowDown className="h-3 w-3 shrink-0" aria-hidden="true" />
                        ) : (
                          <ArrowUp className="h-3 w-3 shrink-0" aria-hidden="true" />
                        ))}
                    </button>
                  </span>
                  <ResizeHandle
                    label={column.label}
                    width={widthOf(column)}
                    onPreview={(width) => setDraft({ id: column.id, width })}
                    onCommit={(width) => commit(column, width)}
                    onFit={() => setFitting(column)}
                  />
                </th>
              );
            })}
            <th scope="col" className="px-2 py-2">
              <span className="sr-only">Spacer</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {window.paddingTop > 0 && (
            <tr aria-hidden="true">
              <td style={{ height: window.paddingTop }} colSpan={cellCount} />
            </tr>
          )}

          {visible.map((row) => {
            const id = String(row['id']);
            return (
              <tr key={id} className="border-t hover:bg-accent/40" style={{ height: ROW_HEIGHT }}>
                {selectable && (
                  <td className="px-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${String(row['opp_number'])}`}
                      className="h-4 w-4"
                      checked={selected.has(id)}
                      onChange={(event) => onSelect(id, event.target.checked)}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn('truncate px-2', column.align === 'right' && 'text-right')}
                  >
                    {column.render(row)}
                  </td>
                ))}
                <td />
              </tr>
            );
          })}

          {window.paddingBottom > 0 && (
            <tr aria-hidden="true">
              <td style={{ height: window.paddingBottom }} colSpan={cellCount} />
            </tr>
          )}

          {rows.length === 0 && (
            <tr>
              <td
                colSpan={cellCount}
                className="px-3 py-10 text-center text-sm text-muted-foreground"
              >
                No opportunities match these filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {fitting !== null && (
        <div
          ref={measureRef}
          aria-hidden="true"
          // `fixed` keeps it out of the wrapper's scroll extent, and
          // `invisible` still lays out — `hidden` would measure zero.
          className="pointer-events-none invisible fixed left-0 top-0 -z-50 text-sm"
        >
          <div data-measure-cell className="w-max whitespace-nowrap text-xs font-medium">
            {fitting.label}
            <span style={{ display: 'inline-block', width: SORT_ICON_WIDTH }} />
          </div>
          {rows.map((row) => (
            <div key={String(row['id'])} data-measure-cell className="w-max whitespace-nowrap">
              {fitting.render(row)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The grab target on a header's right edge.
 *
 * Mouse events rather than pointer events: jsdom ships no `PointerEvent`, and
 * a drag whose only test is "it did not throw" is not a tested drag. Browsers
 * still synthesise these from touch.
 *
 * It is also a real `separator` with arrow keys, because a drag is the one
 * interaction a keyboard user cannot perform at all.
 */
function ResizeHandle({
  label,
  width,
  onPreview,
  onCommit,
  onFit,
}: {
  label: string;
  width: number;
  onPreview: (width: number) => void;
  onCommit: (width: number) => void;
  onFit: () => void;
}): React.ReactElement {
  const release = useRef<(() => void) | null>(null);

  // A drag that outlives its column — a filter change, a column switched off
  // mid-gesture — would otherwise leave both listeners on the document.
  useLayoutEffect(() => () => release.current?.(), []);

  const startDrag = (event: React.MouseEvent<HTMLElement>): void => {
    // Stops the header's sort button from seeing the press.
    event.preventDefault();
    event.stopPropagation();

    const originX = event.clientX;
    const originWidth = width;

    const move = (moveEvent: MouseEvent): void => {
      onPreview(clampColumnWidth(originWidth + moveEvent.clientX - originX));
    };
    const up = (upEvent: MouseEvent): void => {
      release.current?.();
      onCommit(clampColumnWidth(originWidth + upEvent.clientX - originX));
    };

    release.current = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      release.current = null;
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    const step =
      event.key === 'ArrowLeft' ? -KEYBOARD_STEP : event.key === 'ArrowRight' ? KEYBOARD_STEP : 0;
    if (step === 0) return;
    event.preventDefault();
    onCommit(clampColumnWidth(width + step));
  };

  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize the ${label} column`}
      aria-valuenow={width}
      aria-valuemin={MIN_COLUMN_WIDTH}
      aria-valuemax={MAX_COLUMN_WIDTH}
      tabIndex={0}
      title="Drag to resize. Double-click to fit the loaded rows."
      onMouseDown={startDrag}
      onDoubleClick={onFit}
      onKeyDown={onKeyDown}
      className="absolute right-0 top-0 flex h-full w-2 cursor-col-resize touch-none select-none items-stretch justify-center hover:bg-accent focus:outline-none focus-visible:bg-accent"
    >
      <span className="w-px bg-border" aria-hidden="true" />
    </span>
  );
}
